/**
 * AuthorContentCorpusService — CRUD for author_posts subcollection at:
 *   projects/{pid}/{authors|external_authors}/{authorId}/platforms/{platformId}/author_posts/{postId}
 *
 * Two singletons exported (same pattern as AuthorPlatformService):
 *   - `authorContentCorpusService` (default, basePath='authors')
 *   - `externalAuthorContentCorpusService` (basePath='external_authors')
 * Use `getAuthorContentCorpusService(basePath)` to pick at call site.
 *
 * Posts in this subcollection are scraped corpus the AI uses to mirror the
 * author's voice. Sources include BrightData scrapes ('apify' / 'brightdata'
 * marker) or manual paste ('manual'). PlatformPostsTable lets the user
 * curate this list — bulk-delete irrelevant posts before re-analyzing voice.
 *
 * Deletion is chunked at 400 docs per batch (Firestore limit is 500 ops per
 * batch — 400 leaves headroom for serverTimestamp writes if added later).
 */

import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  arrayUnion,
  arrayRemove,
  Timestamp,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { AuthorBasePath } from './AuthorPlatformService';
import type { Post } from '../types';

const BATCH_CHUNK_SIZE = 400;

/**
 * Concurrent Minsky refactor (2026-04-29) — `Post` type is canonical.
 * Author-scoped writes (manual create, curation tags, link-to-topic) live on
 * this service; `useProjectPostExamples` and `useTopicPostExamples` hooks
 * are retired in favour of these methods + collection-group queries.
 */
export interface CreateManualPostInput {
  projectId: string;
  authorId: string;
  platformId: string;
  /** Caller-provided id; service generates one if absent. */
  postId?: string;
  /** PlatformId exactly as recorded on the platform doc. */
  platform: string;
  hook?: string;
  body?: string;
  cta?: string;
  notes?: string;
  title?: string;
  sourceUrl?: string;
  sourceCreatorHandle?: string;
  aiAssets?: Record<string, unknown>;
  exampleForTopicIds?: string[];
  exampleForPillarIds?: string[];
  exampleForPostTypes?: string[];
  addedBy?: string;
}

class AuthorContentCorpusService {
  constructor(private readonly basePath: AuthorBasePath = 'authors') {}

  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private postsCollectionRef(projectId: string, authorId: string, platformId: string) {
    return collection(
      this.db,
      'projects',
      projectId,
      this.basePath,
      authorId,
      'platforms',
      platformId,
      'author_posts',
    );
  }

  /**
   * Bulk-delete a list of post IDs from the corpus. Chunked at 400 ops per
   * batch — caller can pass any size, including 'all' selections of 1000+.
   *
   * Idempotent: deleting a non-existent doc is a no-op in Firestore batch
   * (it just runs the delete and Firestore silently ignores missing docs).
   */
  async deleteAuthorPosts(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    postIds: string[];
  }): Promise<void> {
    const { projectId, authorId, platformId, postIds } = opts;
    if (postIds.length === 0) return;

    const colRef = this.postsCollectionRef(projectId, authorId, platformId);

    for (let i = 0; i < postIds.length; i += BATCH_CHUNK_SIZE) {
      const chunk = postIds.slice(i, i + BATCH_CHUNK_SIZE);
      const batch = writeBatch(this.db);
      for (const postId of chunk) {
        batch.delete(doc(colRef, postId));
      }
      await batch.commit();
    }
  }

  /**
   * Set the multi-tag `exampleForPostTypes` array on a single corpus post.
   * Pass an empty array to demote the post (no tags = no longer an example).
   *
   * Stored on the same doc as the corpus post — the per-platform Examples
   * tab is gone; an "example" is just a corpus row with at least one tag.
   * Per plan §5: AI must NOT auto-write this field; only humans curate.
   */
  async setExampleForPostTypes(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    postId: string;
    types: string[];
  }): Promise<void> {
    const { projectId, authorId, platformId, postId, types } = opts;
    const ref = doc(this.postsCollectionRef(projectId, authorId, platformId), postId);
    await updateDoc(ref, {
      exampleForPostTypes: types,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Convenience: delete ALL posts for a (author, platform) pair. Used by
   * "Clear corpus before re-scrape" action.
   */
  async deleteAllPosts(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
  }): Promise<number> {
    const colRef = this.postsCollectionRef(
      opts.projectId,
      opts.authorId,
      opts.platformId,
    );
    const snap = await getDocs(colRef);
    const ids = snap.docs.map((d) => d.id);
    await this.deleteAuthorPosts({ ...opts, postIds: ids });
    return ids.length;
  }

  /**
   * Concurrent Minsky refactor — create a manual post in the unified Post
   * shape. Replaces the retired `useProjectPostExamples.saveExample` hook +
   * collection. All curation lives in author_posts/* now.
   */
  async createManualPost(input: CreateManualPostInput): Promise<string> {
    const colRef = this.postsCollectionRef(
      input.projectId,
      input.authorId,
      input.platformId,
    );
    const ref = input.postId ? doc(colRef, input.postId) : doc(colRef);
    const id = ref.id;
    const hasExampleTags =
      (input.exampleForTopicIds?.length ?? 0) > 0 ||
      (input.exampleForPillarIds?.length ?? 0) > 0 ||
      (input.exampleForPostTypes?.length ?? 0) > 0;
    const now = Timestamp.now();
    const post: Post & Record<string, unknown> = {
      id,
      projectId: input.projectId,
      authorId: input.authorId,
      authorBasePath: this.basePath,
      kind: 'manual',
      source: 'manual',
      lifecycleStage: hasExampleTags ? 'curated' : 'corpus',
      platforms: input.platform ? [input.platform as any] : null,
      hook: input.hook ?? null,
      body: input.body ?? null,
      cta: input.cta ?? null,
      notes: input.notes ?? null,
      title: input.title ?? null,
      sourceUrl: input.sourceUrl ?? null,
      sourceCreatorHandle: input.sourceCreatorHandle ?? null,
      aiAssets: (input.aiAssets as any) ?? null,
      exampleForTopicIds: input.exampleForTopicIds ?? null,
      exampleForPillarIds: input.exampleForPillarIds ?? null,
      exampleForPostTypes: input.exampleForPostTypes ?? null,
      addedBy: input.addedBy ?? null,
      curatedAt: hasExampleTags ? (now.toDate().toISOString()) : null,
      // publishedAt mirrors createdAt for manual posts so they appear in
      // the platform Posts table (the listing query uses orderBy('publishedAt')
      // which silently drops docs missing this field, and scraped posts
      // store publishedAt as a Firestore Timestamp — keep the same shape).
      publishedAt: now as unknown as string,
      createdAt: now.toDate().toISOString(),
      updatedAt: now.toDate().toISOString(),
    };
    // Strip undefined for Firestore (admin SDK accepts them, web SDK chokes).
    const payload: Record<string, unknown> = { ...post };
    for (const k of Object.keys(payload)) {
      if (payload[k] === undefined) delete payload[k];
    }
    await setDoc(ref, payload);
    return id;
  }

  /**
   * Update content (hook/body/cta/notes/aiAssets) on an existing post —
   * scraped or manual. Concurrent Minsky refactor: scraped corpus is no
   * longer read-only; curators may edit any field. Setting curation tags
   * (exampleFor*Ids) is handled separately to control lifecycleStage.
   */
  async updatePostContent(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    postId: string;
    patch: Partial<Pick<Post,
      'hook' | 'body' | 'cta' | 'notes' | 'title' |
      'sourceUrl' | 'sourceCreatorHandle' | 'aiAssets' | 'platforms'>>;
  }): Promise<void> {
    const ref = doc(
      this.postsCollectionRef(opts.projectId, opts.authorId, opts.platformId),
      opts.postId,
    );
    const patch: Record<string, unknown> = { ...opts.patch, updatedAt: serverTimestamp() };
    for (const k of Object.keys(patch)) {
      if (patch[k] === undefined) delete patch[k];
    }
    await updateDoc(ref, patch);
  }

  /**
   * Toggle a topic tag onto an author_post and flip lifecycleStage to
   * 'curated' (irreversible — once tagged for any topic, the post is
   * curated until ALL exampleFor*Ids arrays are cleared).
   *
   * Symmetric with the bidirectional linkage formerly maintained by
   * `useProjectPostExamples.linkToTopic` (which kept post.linkedTopicIds
   * AND topic.postExampleIds in sync). After concurrent Minsky:
   *   - post.exampleForTopicIds[] is the SINGLE source of truth.
   *   - topic.postExampleIds[] is REMOVED from schema.
   * AI generator queries author_posts WHERE exampleForTopicIds CONTAINS topicId.
   */
  async addTopicExampleTag(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    postId: string;
    topicId: string;
    addedBy?: string;
  }): Promise<void> {
    const ref = doc(
      this.postsCollectionRef(opts.projectId, opts.authorId, opts.platformId),
      opts.postId,
    );
    const patch: Record<string, unknown> = {
      exampleForTopicIds: arrayUnion(opts.topicId),
      lifecycleStage: 'curated',
      updatedAt: serverTimestamp(),
    };
    if (opts.addedBy) patch.addedBy = opts.addedBy;
    if (!('curatedAt' in patch)) patch.curatedAt = serverTimestamp();
    await updateDoc(ref, patch);
  }

  /**
   * Remove a topic tag. Does NOT auto-flip lifecycleStage back to 'corpus'
   * — caller must explicitly demote via `clearAllExampleTags` if desired
   * (a post may be curated for OTHER topics simultaneously).
   */
  async removeTopicExampleTag(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    postId: string;
    topicId: string;
  }): Promise<void> {
    const ref = doc(
      this.postsCollectionRef(opts.projectId, opts.authorId, opts.platformId),
      opts.postId,
    );
    await updateDoc(ref, {
      exampleForTopicIds: arrayRemove(opts.topicId),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Clear ALL curation tags + flip back to 'corpus' (or keep as scraped
   * raw). Symmetric to "demote from example" UI action.
   */
  async clearAllExampleTags(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    postId: string;
  }): Promise<void> {
    const ref = doc(
      this.postsCollectionRef(opts.projectId, opts.authorId, opts.platformId),
      opts.postId,
    );
    await updateDoc(ref, {
      exampleForTopicIds: [],
      exampleForPillarIds: [],
      exampleForPostTypes: [],
      lifecycleStage: 'corpus',
      updatedAt: serverTimestamp(),
    });
  }
}

export const authorContentCorpusService = new AuthorContentCorpusService('authors');
export const externalAuthorContentCorpusService = new AuthorContentCorpusService('external_authors');

/** Pick the right singleton given a basePath. Falls back to self-author. */
export function getAuthorContentCorpusService(
  basePath?: AuthorBasePath,
): AuthorContentCorpusService {
  return basePath === 'external_authors'
    ? externalAuthorContentCorpusService
    : authorContentCorpusService;
}

export { AuthorContentCorpusService };
